import {
  fetchBaseQuery,
  FetchBaseQueryArgs,
  FetchBaseQueryError,
  BaseQueryFn,
} from "@reduxjs/toolkit/query";
import type { RootState } from "../store";
import { logout, setCredentials } from "../slices/authSlice";

// Define expected shape of refresh token response
interface RefreshTokenResponse {
  data: {
    token: string;
    refreshToken: string;
  };
}

interface AuthStorage {
  token: string;
  refreshToken: string;
  userData: any;
}

const apiUrl = process.env.REACT_APP_API_URL as string;

const baseQuery = fetchBaseQuery({
  baseUrl: apiUrl,
  prepareHeaders: (headers, { getState }) => {
    try {
      const storedAuth: any = JSON.parse(
        localStorage.getItem("auth") || "{}"
      ) as AuthStorage;
      const token = storedAuth?.data?.token;
      if (token) {
        headers.set("Authorization", `${token}`);
      }
    } catch (error) {
      console.error("Invalid auth object in localStorage");
    }
    return headers;
  },
});

const customBaseQuery: BaseQueryFn<
  string | FetchBaseQueryArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args as any, api, extraOptions);

  if (result?.error?.status === 401) {
    console.warn("Access token expired. Trying refresh token...");

    const storedAuth: any = JSON.parse(
      localStorage.getItem("auth") || "{}"
    ) as AuthStorage;
    const refreshToken = storedAuth?.data?.refreshToken;
    const userData = storedAuth?.data?.userData;

    const refreshResult = await baseQuery(
      {
        url: "/auth/refresh-token",
        method: "POST",
        body: { refreshToken },
      },
      api,
      extraOptions
    );

    if ((refreshResult as { data?: RefreshTokenResponse }).data) {
      const newTokens = (refreshResult as { data: RefreshTokenResponse }).data;

      api.dispatch(
        setCredentials({
          token: newTokens.data.token,
          refreshToken: newTokens.data.refreshToken,
          userData,
        })
      );

      localStorage.setItem("auth", JSON.stringify({ ...newTokens, userData }));

      // Retry original request with new token
      result = await baseQuery(args as any, api, extraOptions);
    } else {
      api.dispatch(logout());
      localStorage.removeItem("auth");
    }
  }

  return result;
};

export default customBaseQuery;
